import React, { useState } from 'react';
import { getNavIconClass } from '../../../../navigation/clinicNavConfig';
import PatientDetailIcon from './subparts/PatientDetailIcon';
import {
    PATIENT_CONTACT_THEMES,
    PATIENT_DETAIL_PAGE_THEME,
    PATIENT_META_THEMES,
} from './subparts/patientDetailTheme';

const formatGender = (gender) =>
    ({ MALE: 'Male', FEMALE: 'Female', OTHER: 'Other' }[gender] || gender);

const calculateAge = (dob) => {
    if (!dob) {
        return null;
    }

    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age -= 1;
    }

    return age;
};

const formatAddressLine = (parts) => parts.filter(Boolean).join(', ');

function PatientInfoCard({ patient }) {
    const [copiedField, setCopiedField] = useState(null);

    const handleCopy = async (field, value) => {
        if (!value) {
            return;
        }

        try {
            await navigator.clipboard.writeText(value);
        } catch {
            const textArea = document.createElement('textarea');
            textArea.value = value;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
        }

        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 1500);
    };

    const initials = `${patient.firstName?.charAt(0) || '?'}${patient.lastName?.charAt(0) || ''}`;
    const age = calculateAge(patient.dateOfBirth);
    const addressLine = formatAddressLine([
        patient.street,
        patient.city,
        patient.province,
        patient.postalCode,
        patient.country,
    ]);

    const contactItems = [
        {
            key: 'email',
            icon: 'bi-envelope',
            label: 'Email',
            value: patient.email?.trim() || '',
        },
        {
            key: 'cell',
            icon: 'bi-phone',
            label: 'Cell',
            value: patient.cellNumber?.trim() || '',
        },
        {
            key: 'phone',
            icon: 'bi-telephone',
            label: 'Home',
            value: patient.phoneNumber?.trim() || '',
        },
    ].filter((item) => Boolean(item.value));

    return (
        <div className="patient-detail-summary">
            <div className="patient-detail-summary__main">
                <div className="patient-detail-summary__identity">
                    <span
                        className={`ecx-nav-icon ${getNavIconClass(PATIENT_DETAIL_PAGE_THEME)} patient-detail-summary__avatar patient-detail-icon patient-detail-icon--avatar patient-detail-icon--xl`}
                    >
                        {initials}
                    </span>

                    <div className="patient-detail-summary__identity-text">
                        <h2 className="patient-detail-summary__name">
                            {patient.firstName || 'N/A'} {patient.lastName || ''}
                            {patient.preferredName && (
                                <span className="patient-detail-summary__preferred">
                                    ({patient.preferredName})
                                </span>
                            )}
                        </h2>

                        <div className="patient-detail-summary__meta">
                            {patient.pmsId && (
                                <span className="patient-detail-meta-item">
                                    <PatientDetailIcon icon="bi-person-badge" theme={PATIENT_META_THEMES.pms} size="xs" />
                                    PMS ID: {patient.pmsId}
                                </span>
                            )}

                            {patient.dateOfBirth && (
                                <span className="patient-detail-meta-item">
                                    <PatientDetailIcon icon="bi-cake2" theme={PATIENT_META_THEMES.dob} size="xs" />
                                    {patient.dateOfBirth}
                                    {age != null && ` · ${age} yrs`}
                                </span>
                            )}

                            {patient.gender && (
                                <span className="patient-detail-meta-item">
                                    <PatientDetailIcon
                                        icon="bi-gender-ambiguous"
                                        theme={PATIENT_META_THEMES.gender}
                                        size="xs"
                                    />
                                    {formatGender(patient.gender)}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="patient-detail-summary__status">
                    <span
                        className={`clinic-settings-status-pill ${
                            patient.isActive ? 'is-open' : 'is-closed'
                        }`}
                    >
                        <i className={`bi ${patient.isActive ? 'bi-check-circle' : 'bi-x-circle'}`} />
                        {patient.isActive ? 'Active' : 'Inactive'}
                    </span>

                    <span
                        className={`clinic-settings-status-pill ${
                            patient.isWelcome ? 'is-active' : 'is-closed'
                        }`}
                    >
                        <i className={`bi ${patient.isWelcome ? 'bi-envelope-check' : 'bi-envelope'}`} />
                        {patient.isWelcome ? 'Welcome Sent' : 'Welcome Pending'}
                    </span>
                </div>
            </div>

            <div className="patient-detail-summary__grid">
                {contactItems.length > 0 && (
                    <div
                        className={`patient-detail-summary__contacts${
                            contactItems.length === 1
                                ? ' patient-detail-summary__contacts--single'
                                : contactItems.length === 2
                                  ? ' patient-detail-summary__contacts--double'
                                  : ''
                        }`}
                    >
                        {contactItems.map((item) => (
                            <div key={item.key} className="patient-detail-contact-card">
                                <div className="patient-detail-contact-card__label">
                                    <PatientDetailIcon
                                        icon={item.icon}
                                        theme={PATIENT_CONTACT_THEMES[item.key]}
                                        size="xs"
                                    />
                                    {item.label}
                                </div>

                                <div className="patient-detail-contact-card__value">
                                    <span>{item.value}</span>
                                    <button
                                        type="button"
                                        className="patient-files-copy-btn"
                                        onClick={() => handleCopy(item.key, item.value)}
                                        title={`Copy ${item.label.toLowerCase()}`}
                                        aria-label={`Copy ${item.label.toLowerCase()}`}
                                    >
                                        <i
                                            className={`bi ${
                                                copiedField === item.key ? 'bi-check2 text-success' : 'bi-copy'
                                            }`}
                                        />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="patient-detail-summary__address">
                    <div className="patient-detail-contact-card">
                        <div className="patient-detail-contact-card__label">
                            <PatientDetailIcon icon="bi-geo-alt" theme={PATIENT_CONTACT_THEMES.address} size="xs" />
                            Address
                        </div>
                        <div className="patient-detail-contact-card__value patient-detail-contact-card__value--stacked">
                            {addressLine || '—'}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PatientInfoCard;
